/**
 * Capitalize words for display.
 *
 * Turns e.g. "HELLO WORLD" into "Hello World" by lowercasing first, then
 * uppercasing the first Unicode letter at each word boundary.
 *
 * @param s Input string.
 * @returns Capitalized string.
 *
 * @example
 * capitalizeWords("HELLO WORLD"); // "Hello World"
 */
export function capitalizeWords(s: string): string {
	return s.toLowerCase().replace(/\b\p{L}/gu, (c: string) => c.toUpperCase());
}

/**
 * Truncates text to a maximum number of words, appending ellipsis if truncated.
 *
 * @param text - The text to truncate.
 * @param maxWords - Maximum number of words to keep.
 * @returns Truncated text with ellipsis if needed.
 */
export function truncateWords(text: string, maxWords: number): string {
	const words = text.trim().split(/\s+/);

	if (words.length <= maxWords) {
		return text;
	}

	return words.slice(0, maxWords).join(" ") + "...";
}

/**
 * Converts a kebab-case string to PascalCase.
 *
 * @param term - The kebab-case string to convert.
 * @returns The PascalCase version of the input.
 *
 * @example
 * toPascalCase("my-component"); // "MyComponent"
 */
export function kebabToPascalCase(term: string): string {
	return term
		.split("-")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join("");
}
